import type { RouteKpis } from './routeMetrics';
import { InfoPopover } from '../common/InfoPopover';
import type { InfoTopicId } from '../common/infoTopics';
import {
  formatPct,
  formatPerAsm,
  formatSignedPct,
  formatUsd,
} from './formatters';

type Tone = 'positive' | 'negative' | 'warning' | 'neutral';

interface KpiCardSpec {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly hint: string;
  readonly tone: Tone;
  readonly topic?: InfoTopicId;
}

interface KpiCardsProps {
  readonly kpis: RouteKpis;
}

function signTone(value: number): Tone {
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
}

function loadFactorTone(loadFactorPct: number): Tone {
  if (loadFactorPct >= 80) return 'positive';
  if (loadFactorPct >= 65) return 'warning';
  return 'negative';
}

function buildCards(kpis: RouteKpis): readonly KpiCardSpec[] {
  const unitMargin = kpis.rasmUSD - kpis.casmUSD;
  return [
    {
      id: 'revenue',
      label: 'Route Revenue',
      value: formatUsd(kpis.revenueUSD),
      hint: 'Latest simulated day',
      tone: 'neutral',
    },
    {
      id: 'cost',
      label: 'Operating Cost',
      value: formatUsd(kpis.operatingCostUSD),
      hint: 'Fuel, crew, fees, maintenance, ownership',
      tone: 'neutral',
    },
    {
      id: 'profit',
      label: 'Net Profit',
      value: formatUsd(kpis.netProfitUSD),
      hint: 'Revenue − operating cost',
      tone: signTone(kpis.netProfitUSD),
    },
    {
      id: 'load-factor',
      label: 'Avg Load Factor',
      value: formatPct(kpis.loadFactorPct),
      hint: 'Target ≥ 80%',
      tone: loadFactorTone(kpis.loadFactorPct),
    },
    {
      id: 'rasm',
      label: 'RASM',
      value: `${formatPerAsm(kpis.rasmUSD)}/ASM`,
      hint: `Unit margin ${unitMargin >= 0 ? '+' : '−'}${formatPerAsm(Math.abs(unitMargin))}`,
      tone: signTone(unitMargin),
      topic: 'rasm',
    },
    {
      id: 'casm',
      label: 'CASM',
      value: `${formatPerAsm(kpis.casmUSD)}/ASM`,
      hint: 'Cost per available seat mile',
      tone: 'neutral',
      topic: 'casm',
    },
    {
      id: 'margin',
      label: 'Operating Margin',
      value: formatSignedPct(kpis.operatingMarginPct),
      hint: 'Net profit ÷ revenue',
      tone: signTone(kpis.operatingMarginPct),
      topic: 'operatingMargin',
    },
  ];
}

export function KpiCards({ kpis }: KpiCardsProps) {
  const cards = buildCards(kpis);
  return (
    <section className="ri-kpis" aria-label="Route financial overview">
      {cards.map((card) => (
        <article key={card.id} className={`ri-kpi ri-kpi--${card.tone}`}>
          <div className="ri-kpi__head">
            <h3 className="ri-kpi__label">{card.label}</h3>
            {card.topic !== undefined && <InfoPopover topic={card.topic} />}
          </div>
          <p className="ri-kpi__value">{card.value}</p>
          <p className="ri-kpi__hint">{card.hint}</p>
        </article>
      ))}
    </section>
  );
}
